import { StatsGraph } from "./stats_graph";
import { SeriesSet } from "../model/series";
import { SeasonalPeriodicityData } from "../model/seasonal_periodicity_series";
import { SeasonalPeriodicitySeries } from "../model/seasonal_periodicity_series";
import $ from "jquery";

class SeasonalPeriodicityView {
  private container: JQuery<HTMLElement>;

  constructor(container: JQuery<HTMLElement>) {
    this.container = container;
  }

  /**
   * ローディング表示
   */
  showLoading(): void {
    this.container.html(
      '<div class="indicator gr">読み込み中...</div>',
    );
  }

  /**
   * エラー表示
   */
  showError(message: string): void {
    this.container.html(
      `<div class="research_error"><p class="description">${this.escapeHtml(message)}</p></div>`,
    );
  }

  /**
   * グラフと統計情報を描画
   */
  render(data: SeasonalPeriodicityData, keyword?: string, combinationKeyword?: string): void {
    this.container.empty();

    // 1つのチャートを描画
    this.renderChart(data, keyword, combinationKeyword);
  }

  /**
   * グラフを描画
   */
  private renderChart(data: SeasonalPeriodicityData, keyword?: string, combinationKeyword?: string): void {
    const $chartSection = $('<div class="seasonal_periodicity_chart"></div>');

    // 季節指数の統計情報がある場合は、新しい文言を表示
    if (data.seasonal_index && keyword && combinationKeyword) {
      const $seasonalIndexDescription = this.createSeasonalIndexDescription(
        data.seasonal_index,
        keyword,
        combinationKeyword
      );
      $chartSection.append($seasonalIndexDescription);
    }


    // グラフ用のコンテナを作成
    const canvasId = `seasonal_periodicity_${Date.now()}`;
    const $canvas = $(
      `<div id="${canvasId}" class="seasonal_periodicity_graph_canvas"></div>`,
    );
    $chartSection.append($canvas);

    // 年ごとのSeriesを作成
    const { yearlySeries } = SeasonalPeriodicitySeries.convertToYearlySeries(data);

    // SeriesSetを作成
    const seriesSet = new SeriesSet();
    const colors: string[] = [];

    // 今年、2年前、3年前の順にSeriesを追加し、色を設定
    // 一番新しい年（今年）: 青 rgb(31, 145, 207)
    // 2番目に新しい年（2年前）: 緑 rgb(184, 193, 43)
    // 3番目に新しい年（3年前）: 青と緑をグレースケールにしたときより薄いグレー
    // 青のグレースケール: 0.299*31 + 0.587*145 + 0.114*207 ≈ 118
    // 緑のグレースケール: 0.299*184 + 0.587*193 + 0.114*43 ≈ 173
    // 平均: (118 + 173) / 2 ≈ 146
    // より薄くするため、#999999 (rgb(153, 153, 153)) を使用
    yearlySeries.forEach((yearlyData, index) => {
      seriesSet.add(yearlyData.series);
      if (index === 0) {
        // 一番新しい年（今年）：青 rgb(31, 145, 207) = #1F91CF
        colors.push("#1F91CF");
      } else if (index === 1) {
        // 2番目に新しい年（2年前）：緑 rgb(184, 193, 43) = #B8C03B
        colors.push("#B8C03B");
      } else {
        // 3番目に新しい年（3年前）：薄いグレー #999999 (rgb(153, 153, 153))
        // 青と緑のグレースケール平均(146)よりも薄い
        colors.push("#cccccc");
      }
    });

    // DOMに追加してからグラフを描画
    this.container.append($chartSection);

    // グラフを描画（DOMに追加された後に実行）
    if (seriesSet.size() > 0) {
      // DOMに追加されたことを確認するため、次のフレームで描画
      setTimeout(() => {
        const graph = new StatsGraph($canvas, {
          yAxis: StatsGraph.FloatAxis,
          legend: StatsGraph.TextLegend,
          colors: colors.length > 0 ? colors : undefined,
        });

        seriesSet.toArray().forEach((series) => {
          graph.addSeries(series);
        });

        // MonthlyAxisを注入（1-12月のラベルを表示）
        graph.setXAxis(StatsGraph.MonthlyAxis);

        if (!graph.hasNoData()) {
          graph.drawAll();
        }
      }, 0);
    }

    // 注釈を追加
    const $annotations = $('<div class="seasonal_periodicity_annotations" style="font-size: 12px; color: #666; margin-top: 10px;"></div>');

    // ※1の注釈（年平均の定義）
    const $annotation1 = $(
      '<p>※1 <a href="https://info.tabemiru.com#vocabulary" target="_blank">季節指数</a>を利用して算出。過去3年間のデータを3分割し、各年の12ヶ月のうち最も高い月を1件ずつ取得して合計を3で割り、100に対する倍率を算出しています(季節指数は100が平均となります)。</p>',
    );
    $annotations.append($annotation1);

    $chartSection.append($annotations);
  }

  /**
   * 季節指数の説明文を作成
   */
  private createSeasonalIndexDescription(
    seasonalIndex: { peak_month: number | null; average_ratio: number },
    keyword: string,
    combinationKeyword: string,
  ): JQuery<HTMLElement> {
    if (!seasonalIndex.peak_month) {
      return $('<p class="description"></p>'); // データがない場合は空の要素を返す
    }

    const monthName = `${seasonalIndex.peak_month}月`;
    const ratioText = seasonalIndex.average_ratio.toFixed(1);

    const html = `
      <p class="description seasonal_index_description" style="margin-bottom: 10px;">
        <strong>「${this.escapeHtml(keyword)}」</strong>と<strong>「${this.escapeHtml(combinationKeyword)}」</strong>の組み合わせは、<strong>${monthName}</strong>によく検索される傾向にあり、<strong>年平均の${ratioText}倍</strong>の関心を集めています(※1)。
      </p>
    `;

    return $(html);
  }

  /**
   * HTMLエスケープ
   */
  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

export { SeasonalPeriodicityView };
